/**
 * A refused or failed async AI job (TMMAPP-0114 B12 / B2 18; TMMAPI-0152 ask 8): an envelope analysis
 * or a meeting summary that came back FAILED.
 * - The wallet's owner (or a served `firstPurchaseRequired`) gets `AiWalletPrompt` on `WALLET_EMPTY`.
 * - Every other reason, and every other member, reads the reason's one sentence — never a purchase
 *   action and never the owner's wallet state.
 * - `HOLD_EXPIRED` / `PROVIDER_ERROR` / `RECORD_FAILED` read "This couldn't finish — try again.",
 *   with Retry where the feature has one.
 * - A job without a reason (failed before 0152) keeps the feature's own "failed" sentence.
 */
import { Alert, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AiJobFailureReason } from '@/api/aiJobFailure';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import type { TopUpSwap } from '@/features/aiWallet/useTopUpSwap';
import { settingsHref } from '@/features/settings/settingsHref';

/** Each reason's sentence; the three that may succeed on a second ask share "try again". */
const REASON_KEYS: Record<AiJobFailureReason, string> = {
  WALLET_EMPTY: 'ai.jobFailure.WALLET_EMPTY',
  PERSON_CAP: 'ai.jobFailure.PERSON_CAP',
  NO_PRICE: 'ai.jobFailure.NO_PRICE',
  SUSPENDED: 'ai.jobFailure.SUSPENDED',
  AI_DEBT: 'ai.reason.AI_DEBT',
  BUSY: 'ai.reason.BUSY',
  SUBSCRIPTION_INACTIVE: 'ai.reason.SUBSCRIPTION_INACTIVE',
  DAILY_CAP: 'ai.reason.DAILY_CAP',
  AI_DISABLED: 'ai.jobFailure.AI_DISABLED',
  HOLD_EXPIRED: 'ai.jobFailure.retry',
  PROVIDER_ERROR: 'ai.jobFailure.retry',
  RECORD_FAILED: 'ai.jobFailure.retry',
};

const RETRYABLE: ReadonlySet<AiJobFailureReason> = new Set([
  'HOLD_EXPIRED',
  'PROVIDER_ERROR',
  'RECORD_FAILED',
]);

interface AiJobFailureProps {
  failureReason: AiJobFailureReason | null;
  firstPurchaseRequired: boolean;
  /** The reader asked for this job on their own wallet (AI is the owner's, TMMAPI-0146). */
  walletOwner: boolean;
  /** The feature's sentence for a job that carries no reason. */
  fallback: string;
  /**
   * Served with `AI_DEBT`, and only to the owner. Null when absent. Never read from the meter.
   */
  debtMinor?: number | null | undefined;
  /** Ask again; absent where the reader can't (a member). */
  onRetry?: (() => void) | undefined;
  /** The modal this sits in, for the wallet prompt's in-place subscribe card (DLG-005). */
  onCloseHost?: (() => void) | undefined;
  /** The modal's body swap, for the wallet prompt's in-place top-up (C11). */
  swap?: TopUpSwap | undefined;
}

export function AiJobFailure({
  failureReason,
  firstPurchaseRequired,
  walletOwner,
  fallback,
  onRetry,
  onCloseHost,
  swap,
  debtMinor = null,
}: AiJobFailureProps) {
  const { t } = useTranslation();
  if (failureReason === 'WALLET_EMPTY' && (walletOwner || firstPurchaseRequired)) {
    return (
      <AiWalletPrompt
        refusal={{
          reason: 'WALLET_EMPTY',
          availableMinor: null,
          firstPurchaseRequired,
          debtMinor: null,
        }}
        onTryAgain={onRetry ?? (() => undefined)}
        onCloseHost={onCloseHost}
        swap={swap}
      />
    );
  }
  // The owner's debt is the figure on this job, never the meter's. A member gets the sentence only.
  if (failureReason === 'AI_DEBT' && walletOwner) {
    return (
      <AiWalletPrompt
        refusal={{
          reason: 'AI_DEBT',
          availableMinor: null,
          firstPurchaseRequired: false,
          debtMinor,
        }}
        meterDebt={false}
        onTryAgain={onRetry ?? (() => undefined)}
        onCloseHost={onCloseHost}
        swap={swap}
      />
    );
  }
  if (failureReason === null) return <Alert severity="error">{fallback}</Alert>;
  if (failureReason === 'BUSY') {
    return (
      <Alert
        severity="warning"
        data-ai-job-failure={failureReason}
        action={
          onRetry !== undefined ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              {t('aiWallet.tryAgain')}
            </Button>
          ) : null
        }
      >
        {t('ai.reason.BUSY')}
      </Alert>
    );
  }
  if (failureReason === 'SUBSCRIPTION_INACTIVE') {
    return (
      <Alert
        severity="warning"
        data-ai-job-failure={failureReason}
        action={
          walletOwner ? (
            <Button
              component={RouterLink}
              to={settingsHref('subscription')}
              color="inherit"
              size="small"
            >
              {t('plan.renew')}
            </Button>
          ) : null
        }
      >
        {t('ai.reason.SUBSCRIPTION_INACTIVE')}
      </Alert>
    );
  }
  const retryable = RETRYABLE.has(failureReason);
  // The owner reads the suspension as their own account's (item 4); a member, neutrally.
  const text =
    failureReason === 'SUSPENDED' && walletOwner
      ? t('ai.reason.SUSPENDED')
      : t(REASON_KEYS[failureReason]);
  return (
    <Alert
      severity="warning"
      data-ai-job-failure={failureReason}
      action={
        retryable && onRetry !== undefined ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        ) : null
      }
    >
      {text}
    </Alert>
  );
}
